import React, { Component } from 'react';
import { Field } from 'react-final-form';
import classNames from 'classnames';

import { ValidationError } from '../../components';
import RichTextEditor from './RichTextEditor';

import css from './FieldRichTextEditor.module.css';

/**
 * Internal component that handles the Final Form field rendering
 */
const FieldRichTextEditorComponent = props => {
  const {
    rootClassName,
    className,
    id,
    label,
    input,
    meta,
    customErrorText,
    maxLength,
    placeholder,
    hideErrorMessage,
    ...rest
  } = props;

  if (label && !id) {
    throw new Error('id required when a label is given');
  }

  const { value, onChange, onBlur, onFocus } = input;
  const { valid, invalid, touched, error } = meta;

  const errorText = customErrorText || error;
  const hasError = !!customErrorText || !!(touched && invalid && error);
  const fieldMeta = { touched: hasError, error: errorText };

  const classes = classNames(rootClassName || css.root, className);

  return (
    <div className={classes}>
      {label ? (
        <label htmlFor={id} className={css.label}>
          {label}
        </label>
      ) : null}
      
      <RichTextEditor
        id={id}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        onFocus={onFocus}
        placeholder={placeholder}
        maxLength={maxLength}
        className={classNames(css.editor, {
          [css.editorSuccess]: valid,
          [css.editorError]: hasError,
        })}
        {...rest}
      />
      
      {hideErrorMessage ? null : <ValidationError fieldMeta={fieldMeta} />}
    </div>
  );
};

/**
 * Final Form field wrapper for RichTextEditor
 * 
 * @component
 * @param {Object} props
 * @param {string?} props.className - Additional class names
 * @param {string?} props.rootClassName - Override the root class
 * @param {string} props.name - Field name for Final Form
 * @param {string} props.id - Element ID (required when label is provided)
 * @param {string?} props.label - Optional label text
 * @param {string?} props.placeholder - Placeholder text when empty
 * @param {number?} props.maxLength - Maximum character limit (default: 3000)
 * @param {string?} props.customErrorText - Custom error message
 * @param {boolean?} props.hideErrorMessage - Hide error message display
 * @returns {JSX.Element} Final Form Field containing RichTextEditor
 */
class FieldRichTextEditor extends Component {
  componentWillUnmount() {
    if (this.props.onUnmount) {
      this.props.onUnmount();
    }
  }

  render() {
    return <Field component={FieldRichTextEditorComponent} {...this.props} />;
  }
}

export default FieldRichTextEditor;
